'use client';

import { useState } from 'react';

import NextImage from 'next/image';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useGetOsName } from '@hooks/useGetOsName';
import { AppDownloadUrlSourceType, getAppDownloadUrl } from '@domain/routes/getAppDownloadUrl';
import { CloseButton } from '../../Clickables/Controls/CloseButton/CloseButton';
import { ExternalLink } from '../../Clickables/ExternalLink/ExternalLink';
import { Heading } from '../../Heading/Heading';
import { Text } from '../../Text/Text';

import { useIsMobileDevice } from '@hooks/useIsMobileDevice';
import logo from './logo.png';

interface IProps {
  className?: string;
  description?: string;
  buttonText?: string;
  customAppDownloadUrl?: string;
  showOnDesktop?: boolean;
  isUA?: boolean;
}

export const StickyBannerGetApp = ({
  className = '',
  description = 'Open in the Fishbrain App',
  buttonText = 'Get app',
  customAppDownloadUrl,
  showOnDesktop = false,
  isUA = false,
}: IProps) => {
  const [isAppBanner, setIsAppBanner] = useState(true);
  const { currentPage } = useAnalytics();
  const isMobileDevice = useIsMobileDevice();
  const osName = useGetOsName();

  const appDownloadUrl =
    customAppDownloadUrl ??
    getAppDownloadUrl({
      appDownloadSourceType: AppDownloadUrlSourceType.StickyBottomBanner,
      currentPageId: currentPage,
      osName,
      isUA,
    });

  if (!showOnDesktop && !isMobileDevice) {
    return <></>;
  }

  if (!isAppBanner) {
    return <></>;
  }

  return (
    <div
      className={`${className} z-high border-twilight fixed bottom-0 w-full border-t bg-white py-3 pr-4 sm:pr-6`}
      data-testid="StickyBannerGetApp"
    >
      <div className="mx-auto flex flex-row items-center sm:max-w-(--breakpoint-md)">
        <CloseButton
          className="text-abyss"
          variant="controlLakeFogBg"
          onClick={() => {
            setIsAppBanner(false);
          }}
          data-testid="closeAppStickyButton"
          id="close-app-sticky-button"
          width={16}
          height={16}
        />
        <NextImage alt="Fishbrain" src={logo} unoptimized className="max-h-12 max-w-12" />
        <div className="ml-2.5 flex w-1/2 flex-col gap-0.5 pr-2 sm:w-3/5">
          <Heading variant="xs">Fishbrain</Heading>
          <Text variant="sm" className="mb-0 truncate">
            {description}
          </Text>
        </div>
        <ExternalLink
          href={appDownloadUrl}
          variant="buttonPrimary"
          className="mx-auto w-1/4 whitespace-nowrap sm:mr-4 sm:ml-auto sm:w-1/5"
          onClick={() => {
            trackEvent({
              name: 'mykiss_cta_button_clicked',
              properties: {
                type: 'download',
                source: AppDownloadUrlSourceType.StickyBottomBanner,
                page: currentPage,
              },
            });
          }}
        >
          {buttonText}
        </ExternalLink>
      </div>
    </div>
  );
};
